import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { money, thaiDate } from '../lib/format'

/**
 * Multi-currency invoicing (part 139).
 *
 * The rate is basis points of quote per 1 base: 35.42 THB per USD is 354200.
 * Amounts are parsed to integer satang and converted with BigInt in the
 * browser so the preview matches the server exactly — no floating point.
 */

interface FXRate {
  id: string
  base: string
  quote: string
  rate_bp: number
  as_of: string
  source: string
}

interface ForeignInvoice {
  id: string
  number: string
  currency: string
  fx_rate_bp: number
  total_foreign_satang: number
  subtotal_satang: number
  vat_satang: number
  total_satang: number
  status: string
  issued_at: string
  due_at: string
  fx_rate_as_of?: string
  fx_rate_source?: string
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'SGD', 'CNY', 'HKD', 'AUD', 'CHF']
const RATE_KEY = ['fx-rates']

function isoDay(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** "1,000.00" -> 100000n satang (the minor unit of the selected currency). */
function parseAmountSatang(raw: string): bigint | null {
  const s = raw.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  return BigInt(whole) * 100n + BigInt((frac + '00').slice(0, 2))
}

/** Half-up integer conversion, identical to fx.Convert on the server. */
function convertSatang(amount: bigint, rateBP: number): bigint {
  return (amount * BigInt(rateBP) + 5000n) / 10000n
}

/** 354200 -> "35.4200" without a float round-trip. */
function rateDecimal(bp: number): string {
  const v = Math.max(0, Math.trunc(bp))
  return `${Math.trunc(v / 10000)}.${String(v % 10000).padStart(4, '0')}`
}

/** 100000 -> "1,000.00" in the foreign currency's major unit, for display only. */
function foreignAmount(satang: number): string {
  return (satang / 100).toLocaleString('th-TH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** 354200 -> 282, displayed as 0.0282 — the inverse direction. */
function inverseBP(bp: number): number {
  if (bp <= 0) return 0
  return Math.floor((200_000_000 + bp) / (2 * bp))
}

const statusLabel: Record<string, string> = {
  open: 'รอชำระ', paid: 'ชำระแล้ว', draft: 'ฉบับร่าง', void: 'ยกเลิก',
}

export function FX() {
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin'
  const qc = useQueryClient()

  const rates = useQuery({
    queryKey: RATE_KEY,
    queryFn: () => list<FXRate>('/v1/fx/rates?limit=200'),
    staleTime: 30_000,
  })

  const today = isoDay()

  const [base, setBase] = useState('USD')
  const [quote, setQuote] = useState('THB')
  const [rate, setRate] = useState('35.42')
  const [asOf, setAsOf] = useState(isoDay())
  const [source, setSource] = useState('manual')

  const saveRate = useMutation({
    mutationFn: () => apiWrite<FXRate>('/v1/fx/rates', 'POST', {
      base, quote, rate, as_of: asOf, source,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: RATE_KEY }),
  })

  // ------------------------------------------------------------ converter
  const [amount, setAmount] = useState('1000.00')
  const [convertCcy, setConvertCcy] = useState('USD')

  const latest = useMemo(() => {
    const rows = rates.data ?? []
    return rows.find((r) => r.base === convertCcy && r.quote === 'THB' && r.as_of <= today)
  }, [rates.data, convertCcy, today])

  const amountSatang = parseAmountSatang(amount)
  const converted = amountSatang !== null && latest && amountSatang > 0n
    ? convertSatang(amountSatang, latest.rate_bp)
    : null
  const convertedDisplay = converted !== null && converted <= BigInt(Number.MAX_SAFE_INTEGER)
    ? money(Number(converted))
    : null

  // -------------------------------------------------------- issue invoice
  const [issueCcy, setIssueCcy] = useState('USD')
  const [issueAmount, setIssueAmount] = useState('1000.00')
  const [issueStart, setIssueStart] = useState(isoDay())
  const [issueEnd, setIssueEnd] = useState(isoDay(30))
  const [issuePlan, setIssuePlan] = useState('growth')
  const [issueDue, setIssueDue] = useState('')

  const issueSatang = parseAmountSatang(issueAmount)
  const issueSafe = issueSatang !== null && issueSatang > 0n && issueSatang <= BigInt(Number.MAX_SAFE_INTEGER)
    ? Number(issueSatang)
    : null

  const issue = useMutation({
    mutationFn: () => apiWrite<ForeignInvoice>('/v1/billing/invoices/foreign', 'POST', {
      currency: issueCcy,
      total_foreign_satang: issueSafe,
      period_start: issueStart,
      period_end: issueEnd,
      plan_id: issuePlan,
      due_at: issueDue,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] })
      void qc.invalidateQueries({ queryKey: ['billing'] })
    },
  })

  const rows = rates.data ?? []

  return (
    <>
      <PageHead
        title="สกุลเงินและอัตราแลกเปลี่ยน"
        sub="ล็อกอัตรา ณ วันที่ออกใบแจ้งหนี้ · แปลงเป็นสตางค์จำนวนเต็ม · ไม่ใช้ทศนิยมลอยตัว"
        actions={<span className="chip">{rows.length} อัตราที่บันทึกไว้</span>}
      />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <span className="card-label">ประวัติอัตราแลกเปลี่ยน</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>เรียงจากวันที่ใหม่ที่สุด</span>
          </div>
          {rates.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={180} /></div>
          ) : rates.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>โหลดอัตราแลกเปลี่ยนไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
          ) : rows.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่มีอัตราแลกเปลี่ยน" body="บันทึกอัตราคู่สกุลเงินก่อน แล้วจึงออกใบแจ้งหนี้ต่างประเทศได้" />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead>
                  <tr><th>คู่สกุลเงิน</th><th>อัตรา</th><th>วันที่</th><th>แหล่งที่มา</th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ cursor: 'default' }}>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>{r.base}/{r.quote}</td>
                      <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>
                        1 {r.base} = {rateDecimal(r.rate_bp)} {r.quote}
                      </td>
                      <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDate(r.as_of)}</td>
                      <td className="dim">{r.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">บันทึกอัตราใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            กรอกอัตราเป็นทศนิยมได้ไม่เกิน 4 ตำแหน่ง ระบบเก็บเป็นจำนวนเต็มฐานหมื่น
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => { e.preventDefault(); if (canManage) saveRate.mutate() }}
          >
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="fx-base">สกุลเงินฐาน</label>
                <select id="fx-base" className="input" value={base} disabled={!canManage}
                  onChange={(e) => setBase(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="fx-quote">สกุลเงินปลายทาง</label>
                <select id="fx-quote" className="input" value={quote} disabled={!canManage}
                  onChange={(e) => setQuote(e.target.value)}>
                  {['THB', ...CURRENCIES].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="fx-rate">อัตรา (1 {base} = ? {quote})</label>
              <input id="fx-rate" className="input mono" inputMode="decimal" placeholder="35.42"
                value={rate} disabled={!canManage} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="fx-date">วันที่</label>
                <input id="fx-date" className="input" type="date" value={asOf} disabled={!canManage}
                  onChange={(e) => setAsOf(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="fx-source">แหล่งที่มา</label>
                <input id="fx-source" className="input" placeholder="manual" value={source}
                  disabled={!canManage} onChange={(e) => setSource(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={!canManage || saveRate.isPending}
              style={{ width: '100%' }}>
              {saveRate.isPending ? 'กำลังบันทึก…' : 'บันทึกอัตรา'}
            </button>
          </form>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>เฉพาะเจ้าของและผู้ดูแลระบบเท่านั้นที่บันทึกอัตราได้</div>
          ) : null}
          {saveRate.isError ? (
            <div className="form-error" style={{ marginTop: 10 }}>
              {saveRate.error instanceof Error ? saveRate.error.message : 'บันทึกอัตราไม่สำเร็จ'}
            </div>
          ) : null}
          {saveRate.isSuccess ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
              บันทึกแล้ว · 1 {saveRate.data.base} = {rateDecimal(saveRate.data.rate_bp)} {saveRate.data.quote}
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">แปลงยอดเป็นบาท</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
            ใช้เฉพาะการคำนวณในหน้าเว็บ · ผลลัพธ์เป็นสตางค์จำนวนเต็ม
          </div>
          <div className="row" style={{ gap: 10, marginTop: 14 }}>
            <div className="field" style={{ flex: 2 }}>
              <label htmlFor="fx-amount">จำนวนเงิน</label>
              <input id="fx-amount" className="input mono" inputMode="decimal" placeholder="1000.00"
                value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="fx-ccy">สกุลเงิน</label>
              <select id="fx-ccy" className="input" value={convertCcy}
                onChange={(e) => setConvertCcy(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {latest ? (
            <>
              <div className="row" style={{ marginTop: 4, fontSize: 12 }}>
                <span className="chip gold">1 {latest.base} = {rateDecimal(latest.rate_bp)} THB</span>
                <span className="dim" style={{ marginLeft: 'auto' }}>อัตรา {thaiDate(latest.as_of)}</span>
              </div>
              <div className="rule" style={{ margin: '14px 0' }} />
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="dim" style={{ fontSize: 12 }}>เทียบเป็นเงินบาท</span>
                <span className="display-m tnum" style={{ marginLeft: 'auto' }}>{convertedDisplay ?? '—'}</span>
              </div>
              <div className="row" style={{ marginTop: 10, fontSize: 12 }}>
                <span className="dim">จำนวนเต็ม</span>
                <span className="mono tnum" style={{ marginLeft: 'auto' }}>
                  {converted !== null ? `${converted.toString()} สตางค์` : '—'}
                </span>
              </div>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                กลับกัน · 1 THB = {rateDecimal(inverseBP(latest.rate_bp))} {latest.base}
              </div>
              {amountSatang === null ? (
                <div className="form-error" style={{ marginTop: 10 }}>จำนวนเงินไม่ถูกต้อง — ใช้ตัวเลขทศนิยมไม่เกิน 2 ตำแหน่ง</div>
              ) : null}
            </>
          ) : (
            <div className="dim" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.9 }}>
              ยังไม่มีอัตรา {convertCcy}/THB ที่มีผลถึงวันนี้ — บันทึกอัตราทางด้านขวาก่อน
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">ออกใบแจ้งหนี้ต่างประเทศ</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            ยอดที่กรอกเป็นยอดรวมภาษีมูลค่าเพิ่มแล้ว · ระบบจะล็อกอัตราล่าสุดไว้กับใบแจ้งหนี้ทันที
          </div>
          <form
            style={{ marginTop: 12 }}
            onSubmit={(e) => { e.preventDefault(); if (canManage && issueSafe !== null) issue.mutate() }}
          >
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-ccy">สกุลเงิน</label>
                <select id="inv-ccy" className="input" value={issueCcy} disabled={!canManage}
                  onChange={(e) => setIssueCcy(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-amount">ยอดรวมต่างประเทศ</label>
                <input id="inv-amount" className="input mono" inputMode="decimal" placeholder="1000.00"
                  value={issueAmount} disabled={!canManage} onChange={(e) => setIssueAmount(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-start">เริ่มรอบ</label>
                <input id="inv-start" className="input" type="date" value={issueStart} disabled={!canManage}
                  onChange={(e) => setIssueStart(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-end">สิ้นรอบ</label>
                <input id="inv-end" className="input" type="date" value={issueEnd} disabled={!canManage}
                  onChange={(e) => setIssueEnd(e.target.value)} />
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-plan">แพ็กเกจ</label>
                <select id="inv-plan" className="input" value={issuePlan} disabled={!canManage}
                  onChange={(e) => setIssuePlan(e.target.value)}>
                  <option value="starter">Starter</option>
                  <option value="growth">Growth</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="inv-due">ครบกำหนด</label>
                <input id="inv-due" className="input" type="date" value={issueDue} disabled={!canManage}
                  onChange={(e) => setIssueDue(e.target.value)} />
              </div>
            </div>
            <button className="btn btn-primary" type="submit"
              disabled={!canManage || issue.isPending || issueSafe === null} style={{ width: '100%' }}>
              {issue.isPending ? 'กำลังออกใบแจ้งหนี้…' : 'ออกใบแจ้งหนี้และล็อกอัตรา'}
            </button>
          </form>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>เฉพาะเจ้าของและผู้ดูแลระบบเท่านั้นที่ออกใบแจ้งหนี้ได้</div>
          ) : null}
          {issue.isError ? (
            <div className="form-error" style={{ marginTop: 10 }}>
              {issue.error instanceof Error ? issue.error.message : 'ออกใบแจ้งหนี้ไม่สำเร็จ'}
            </div>
          ) : null}
          {issue.isSuccess ? (
            <div style={{ marginTop: 12 }}>
              <div className="row">
                <span className="chip gold">ออกแล้ว</span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 12 }}>{issue.data.number}</span>
              </div>
              <dl className="kv" style={{ marginTop: 10, gridTemplateColumns: '1fr auto' }}>
                <dt>{issue.data.currency} {foreignAmount(issue.data.total_foreign_satang)}</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>{money(issue.data.total_satang)}</dd>
                <dt>อัตราที่ล็อก</dt>
                <dd className="mono" style={{ textAlign: 'right' }}>{rateDecimal(issue.data.fx_rate_bp)}</dd>
                <dt>VAT 7% (ในยอด)</dt>
                <dd className="tnum" style={{ textAlign: 'right' }}>{money(issue.data.vat_satang)}</dd>
                <dt>สถานะ</dt>
                <dd style={{ textAlign: 'right' }}>{statusLabel[issue.data.status] ?? issue.data.status}</dd>
              </dl>
              <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
                ใบนี้จะใช้ {rateDecimal(issue.data.fx_rate_bp)} ตลอดไป แม้อัตราตลาดจะเปลี่ยนภายหลัง
                {issue.data.fx_rate_as_of ? ` · อัตราวันที่ ${thaiDate(issue.data.fx_rate_as_of)}` : ''}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">เหตุใดจึงต้องล็อกอัตรา</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ใบแจ้งหนี้เก็บอัตราที่ใช้ ณ วันออก พร้อมยอดต่างประเทศและยอดบาทที่คำนวณแล้ว</li>
          <li>การแปลงใช้จำนวนเต็มฐานหมื่น (basis points) และปัดครึ่งขึ้นเฉพาะหลักสุดท้าย</li>
          <li>ยอดต่างประเทศและยอดบาทจึงกระทบกันได้ตรงเสมอ แม้ตรวจซ้ำภายหลัง</li>
          <li>อัตราใหม่มีผลกับใบใหม่เท่านั้น ไม่ย้อนไปแก้ใบที่ออกแล้ว</li>
          <li>การเติม VAT ใช้วิธีแยกจากยอดรวม เพื่อให้ผลรวมยังเท่ากับยอดที่แปลงได้พอดี</li>
        </ul>
      </div>
    </>
  )
}
